import { removeAuth0Prefix } from '../../../users/removeAuth0Prefix/removeAuth0Prefix'
import { formatName } from '../../util/format-name'

/** Minimal shape of the owner snapshot stored on contacts/quotes/deals/tasks. */
export interface OwnerNameSnapshot {
  id?: string
  firstName?: string
  lastName?: string
}

/** Minimal shape of an entry in the live users map (`useUsers()` rows, keyed prefix-less). */
export interface OwnerNameUser {
  user_metadata?: {
    firstName?: string
    lastName?: string
  }
}

/**
 * Resolves the display name for a document owner, preferring the *live* user record over the
 * denormalized snapshot frozen onto the document when it was written.
 *
 * `owner` is a point-in-time copy of the user's name (see `getOwnerFromUser`/`setOwner`), kept in
 * sync only by the best-effort batch tasks `updateUser` fires on a rename. When a seat is renamed
 * or handed to someone else, a snapshot that missed that fan-out keeps rendering the previous
 * estimator's name — which is #21800. Reading through the users map makes the display immune to
 * that, and falls back to the snapshot for owners who are no longer in the list (deactivated or
 * removed users are absent from `searchUsers`).
 */
export function getOwnerDisplayName({
  owner,
  users
}: {
  owner?: OwnerNameSnapshot | null
  users?: Record<string, OwnerNameUser> | null
}): string {
  if (!owner) {
    return ''
  }

  const user = owner.id ? users?.[removeAuth0Prefix(owner.id)] : undefined

  return formatName(user?.user_metadata) || formatName(owner)
}
